/**
 * Node Event Service
 * Centralized event system for tracking node lifecycle and operations
 */

import { WorkflowNode } from '@flow-studio/types';

// Event types
export type NodeEventType =
  | 'NodeAdded'
  | 'NodeUpdated'
  | 'NodeDeleted'
  | 'NodeSelected'
  | 'NodeDeselected'
  | 'NodeMoved'
  | 'NodeDuplicated'
  | 'NodeConnected'
  | 'NodeDisconnected'
  | 'NodeParametersChanged'
  | 'NodeStateChanged'
  | 'NodeExecutionStarted'
  | 'NodeExecutionCompleted'
  | 'NodeExecutionFailed';

// Event payload
export interface NodeEvent {
  type: NodeEventType;
  timestamp: number;
  node: WorkflowNode;
  metadata?: Record<string, any>;
}

// Event handler type
export type NodeEventHandler = (event: NodeEvent) => void;

/**
 * Node Event Service
 * Singleton service for managing node events
 */
export class NodeEventService {
  private static instance: NodeEventService;
  private handlers: Map<NodeEventType, Set<NodeEventHandler>> = new Map();
  private globalHandlers: Set<NodeEventHandler> = new Set();
  private eventHistory: NodeEvent[] = [];
  private maxHistorySize: number = 100;

  private constructor() {
    // Private constructor for singleton
  }

  /**
   * Get singleton instance
   */
  static getInstance(): NodeEventService {
    if (!NodeEventService.instance) {
      NodeEventService.instance = new NodeEventService();
    }
    return NodeEventService.instance;
  }

  /**
   * Subscribe to specific event type
   */
  on(eventType: NodeEventType, handler: NodeEventHandler): () => void {
    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, new Set());
    }
    this.handlers.get(eventType)!.add(handler);

    // Return unsubscribe function
    return () => {
      this.off(eventType, handler);
    };
  }

  /**
   * Subscribe to all events
   */
  onAll(handler: NodeEventHandler): () => void {
    this.globalHandlers.add(handler);

    // Return unsubscribe function
    return () => {
      this.globalHandlers.delete(handler);
    };
  }

  /**
   * Unsubscribe from event type
   */
  off(eventType: NodeEventType, handler: NodeEventHandler): void {
    const handlers = this.handlers.get(eventType);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  /**
   * Emit an event
   */
  emit(eventType: NodeEventType, node: WorkflowNode, metadata?: Record<string, any>): void {
    const event: NodeEvent = {
      type: eventType,
      timestamp: Date.now(),
      node,
      metadata,
    };

    // Add to history
    this.addToHistory(event);

    // Call type-specific handlers
    const typeHandlers = this.handlers.get(eventType);
    if (typeHandlers) {
      typeHandlers.forEach(handler => {
        try {
          handler(event);
        } catch (error) {
          console.error(`Error in ${eventType} handler:`, error);
        }
      });
    }

    // Call global handlers
    this.globalHandlers.forEach(handler => {
      try {
        handler(event);
      } catch (error) {
        console.error(`Error in global event handler:`, error);
      }
    });
  }

  /**
   * Get event history
   */
  getHistory(filterType?: NodeEventType): NodeEvent[] {
    if (filterType) {
      return this.eventHistory.filter(e => e.type === filterType);
    }
    return [...this.eventHistory];
  }

  /**
   * Clear event history
   */
  clearHistory(): void {
    this.eventHistory = [];
  }

  /**
   * Get event statistics
   */
  getStatistics(): Record<NodeEventType, number> {
    const stats: Partial<Record<NodeEventType, number>> = {};

    this.eventHistory.forEach(event => {
      if (!stats[event.type]) {
        stats[event.type] = 0;
      }
      stats[event.type]!++;
    });

    return stats as Record<NodeEventType, number>;
  }

  /**
   * Add event to history with size limit
   */
  private addToHistory(event: NodeEvent): void {
    this.eventHistory.push(event);

    // Maintain max size
    if (this.eventHistory.length > this.maxHistorySize) {
      this.eventHistory.shift();
    }
  }

  /**
   * Clear all handlers
   */
  clearAllHandlers(): void {
    this.handlers.clear();
    this.globalHandlers.clear();
  }
}

// Export singleton instance
export const nodeEventService = NodeEventService.getInstance();

// Convenience methods for common events
export const nodeEvents = {
  added: (node: WorkflowNode, metadata?: Record<string, any>) =>
    nodeEventService.emit('NodeAdded', node, metadata),

  updated: (node: WorkflowNode, metadata?: Record<string, any>) =>
    nodeEventService.emit('NodeUpdated', node, metadata),

  deleted: (node: WorkflowNode, metadata?: Record<string, any>) =>
    nodeEventService.emit('NodeDeleted', node, metadata),

  selected: (node: WorkflowNode, metadata?: Record<string, any>) =>
    nodeEventService.emit('NodeSelected', node, metadata),

  deselected: (node: WorkflowNode, metadata?: Record<string, any>) =>
    nodeEventService.emit('NodeDeselected', node, metadata),

  moved: (node: WorkflowNode, metadata?: Record<string, any>) =>
    nodeEventService.emit('NodeMoved', node, metadata),

  parametersChanged: (node: WorkflowNode, metadata?: Record<string, any>) =>
    nodeEventService.emit('NodeParametersChanged', node, metadata),

  stateChanged: (node: WorkflowNode, metadata?: Record<string, any>) =>
    nodeEventService.emit('NodeStateChanged', node, metadata),
};
